import { supabase, TABLES } from '../config/supabase';
import { accountService } from './accountService';
import { contactsService } from './contactsService';
import { opportunitiesService } from './opportunitiesService';

/**
 * CSV Import Service
 * Handles parsing, validation, and importing of CSV files for Accounts and Contacts
 */

/**
 * Parse CSV file to array of objects
 * @param {File} file - CSV file
 * @returns {Promise<Array>} Parsed data
 */
export const parseCSV = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (e) => {
            try {
                const text = e.target.result;
                const lines = text.split(/\r?\n/).filter(line => line.trim());

                if (lines.length < 2) {
                    reject(new Error('CSV file must contain headers and at least one data row'));
                    return;
                }

                // Parse headers
                const headers = lines[0].split(',').map(h => h.trim());

                // Parse data rows
                const data = [];
                for (let i = 1; i < lines.length; i++) {
                    const values = parseCSVLine(lines[i]);

                    if (values.length !== headers.length) {
                        console.warn(`Row ${i + 1} has ${values.length} columns, expected ${headers.length}. Skipping.`);
                        continue;
                    }

                    const row = {};
                    headers.forEach((header, index) => {
                        row[header] = values[index]?.trim() || null;
                    });
                    data.push(row);
                }

                resolve(data);
            } catch (error) {
                reject(error);
            }
        };

        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsText(file, 'UTF-8');
    });
};

/**
 * Parse a single CSV line handling quoted values
 */
const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
        const char = line[i];

        if (char === '"') {
            inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
            result.push(current);
            current = '';
        } else {
            current += char;
        }
    }
    result.push(current);

    return result;
};

/**
 * Parse date from multiple formats
 * Supports: YYYY-MM-DD, DD-MMM-YYYY
 */
const parseDate = (dateStr) => {
    if (!dateStr) return null;

    // Try ISO format first (YYYY-MM-DD)
    const isoMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoMatch) {
        return dateStr;
    }

    // Try DD-MMM-YYYY format
    const monthMap = {
        'jan': '01', 'feb': '02', 'mar': '03', 'apr': '04',
        'may': '05', 'jun': '06', 'jul': '07', 'aug': '08',
        'sep': '09', 'oct': '10', 'nov': '11', 'dec': '12'
    };

    const ddMmmYyyy = dateStr.match(/^(\d{2})-([a-z]{3})-(\d{4})$/i);
    if (ddMmmYyyy) {
        const day = ddMmmYyyy[1];
        const month = monthMap[ddMmmYyyy[2].toLowerCase()];
        const year = ddMmmYyyy[3];

        if (month) {
            return `${year}-${month}-${day}`;
        }
    }

    return null;
};

/**
 * Validate accounts CSV data
 */
export const validateAccountsCSV = (data) => {
    const errors = [];
    const requiredFields = ['name'];

    if (!Array.isArray(data) || data.length === 0) {
        return { valid: false, errors: ['No data to import'] };
    }

    // Check headers
    const headers = Object.keys(data[0]);
    const missingFields = requiredFields.filter(field => !headers.includes(field));

    if (missingFields.length > 0) {
        errors.push(`Missing required columns: ${missingFields.join(', ')}`);
    }

    // Validate each row
    data.forEach((row, index) => {
        const rowNum = index + 2; // +2 because index starts at 0 and first row is header

        if (!row.name || row.name.trim() === '') {
            errors.push(`Row ${rowNum}: Account name is required`);
        }

        // Validate URLs if provided
        if (row.website && row.website !== '' && !isValidUrl(row.website)) {
            errors.push(`Row ${rowNum}: Invalid website URL`);
        }

        if (row.linkedin_url && row.linkedin_url !== '' && !isValidUrl(row.linkedin_url)) {
            errors.push(`Row ${rowNum}: Invalid LinkedIn URL`);
        }
    });

    return {
        valid: errors.length === 0,
        errors
    };
};

/**
 * Validate contacts CSV data
 */
export const validateContactsCSV = (data) => {
    const errors = [];
    const requiredFields = ['first_name', 'last_name', 'account_name'];

    if (!Array.isArray(data) || data.length === 0) {
        return { valid: false, errors: ['No data to import'] };
    }

    // Check headers
    const headers = Object.keys(data[0]);
    const missingFields = requiredFields.filter(field => !headers.includes(field));

    if (missingFields.length > 0) {
        errors.push(`Missing required columns: ${missingFields.join(', ')}`);
    }

    // Validate each row
    data.forEach((row, index) => {
        const rowNum = index + 2;

        if (!row.first_name || row.first_name.trim() === '') {
            errors.push(`Row ${rowNum}: First name is required`);
        }

        if (!row.last_name || row.last_name.trim() === '') {
            errors.push(`Row ${rowNum}: Last name is required`);
        }

        if (!row.account_name || row.account_name.trim() === '') {
            errors.push(`Row ${rowNum}: Account name is required`);
        }

        // Validate email format if provided
        if (row.email && row.email !== '' && !isValidEmail(row.email)) {
            errors.push(`Row ${rowNum}: Invalid email format`);
        }
    });

    return {
        valid: errors.length === 0,
        errors
    };
};

/**
 * Detect duplicate accounts by name
 */
export const detectAccountDuplicates = async (data) => {
    try {
        const result = await accountService.getAccounts();

        if (!result.success) {
            return { success: false, error: result.message };
        }

        const existingAccounts = result.data;
        const duplicates = [];

        data.forEach((row, index) => {
            const existing = existingAccounts.find(
                acc => acc.name.toLowerCase() === row.name.toLowerCase()
            );

            if (existing) {
                duplicates.push({
                    rowIndex: index,
                    csvData: row,
                    existingData: existing
                });
            }
        });

        return {
            success: true,
            duplicates,
            hasDuplicates: duplicates.length > 0
        };
    } catch (error) {
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * Detect duplicate contacts by email
 */
export const detectContactDuplicates = async (data) => {
    try {
        const result = await contactsService.getAll();

        if (!result.success) {
            return { success: false, error: result.message };
        }

        const existingContacts = result.data;
        const duplicates = [];

        data.forEach((row, index) => {
            if (row.email && row.email.trim() !== '') {
                const existing = existingContacts.find(
                    contact => contact.email?.toLowerCase() === row.email.toLowerCase()
                );

                if (existing) {
                    duplicates.push({
                        rowIndex: index,
                        csvData: row,
                        existingData: existing
                    });
                }
            }
        });

        return {
            success: true,
            duplicates,
            hasDuplicates: duplicates.length > 0
        };
    } catch (error) {
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * Import accounts with option to update existing
 */
export const importAccounts = async (data, updateOptions = {}) => {
    const results = {
        total: data.length,
        created: 0,
        updated: 0,
        skipped: 0,
        failed: 0,
        errors: []
    };

    for (let i = 0; i < data.length; i++) {
        const row = data[i];
        const rowNum = i + 2;

        try {
            // Check if this row should be updated or skipped
            const shouldUpdate = updateOptions[i] === true;
            const shouldSkip = updateOptions[i] === false;

            // Prepare account data
            const accountData = {
                name: row.name?.trim(),
                industry: row.industry?.trim() || null,
                city: row.city?.trim() || null,
                country: row.country?.trim() || null,
                website: row.website?.trim() || null,
                linkedin_url: row.linkedin_url?.trim() || null,
                tax_id: row.tax_id?.trim() || null,
                notes: row.notes?.trim() || null
            };

            // Check for existing account
            const searchResult = await accountService.searchAccounts(accountData.name, { limit: 1 });
            const existingAccount = searchResult.success && searchResult.data.length > 0
                ? searchResult.data.find(acc => acc.name.toLowerCase() === accountData.name.toLowerCase())
                : null;

            if (existingAccount) {
                if (shouldSkip) {
                    results.skipped++;
                    continue;
                } else if (shouldUpdate) {
                    const updateResult = await accountService.updateAccount(existingAccount.id, accountData);
                    if (updateResult.success) {
                        results.updated++;
                    } else {
                        results.failed++;
                        results.errors.push(`Row ${rowNum}: ${updateResult.message}`);
                    }
                } else {
                    results.skipped++;
                }
            } else {
                // Create new account
                const createResult = await accountService.createAccount(accountData);
                if (createResult.success) {
                    results.created++;
                } else {
                    results.failed++;
                    results.errors.push(`Row ${rowNum}: ${createResult.message}`);
                }
            }
        } catch (error) {
            results.failed++;
            results.errors.push(`Row ${rowNum}: ${error.message}`);
        }
    }

    return results;
};

/**
 * Import contacts with option to update existing
 */
export const importContacts = async (data, updateOptions = {}) => {
    const results = {
        total: data.length,
        created: 0,
        updated: 0,
        skipped: 0,
        failed: 0,
        errors: []
    };

    // First, get all accounts to match account names
    const accountsResult = await accountService.getAccounts();
    if (!accountsResult.success) {
        results.errors.push('Failed to fetch accounts for matching');
        return results;
    }

    const accounts = accountsResult.data;

    for (let i = 0; i < data.length; i++) {
        const row = data[i];
        const rowNum = i + 2;

        try {
            // Find matching account
            const account = accounts.find(
                acc => acc.name.toLowerCase() === row.account_name?.toLowerCase()
            );

            if (!account) {
                results.failed++;
                results.errors.push(`Row ${rowNum}: Account "${row.account_name}" not found`);
                continue;
            }

            // Check if this row should be updated or skipped
            const shouldUpdate = updateOptions[i] === true;
            const shouldSkip = updateOptions[i] === false;

            // Prepare contact data
            const contactData = {
                account_id: account.id,
                first_name: row.first_name?.trim(),
                last_name: row.last_name?.trim(),
                email: row.email?.trim() || null,
                phone: row.phone?.trim() || null,
                title: row.title?.trim() || null,
                is_private: row.is_private?.toLowerCase() === 'true'
            };

            // Check for existing contact by email
            let existingContact = null;
            if (contactData.email) {
                const allContactsResult = await contactsService.getAll();
                if (allContactsResult.success) {
                    existingContact = allContactsResult.data.find(
                        c => c.email?.toLowerCase() === contactData.email.toLowerCase()
                    );
                }
            }

            if (existingContact) {
                if (shouldSkip) {
                    results.skipped++;
                    continue;
                } else if (shouldUpdate) {
                    const updateResult = await contactsService.update(existingContact.id, contactData);
                    if (updateResult.success) {
                        results.updated++;
                    } else {
                        results.failed++;
                        results.errors.push(`Row ${rowNum}: ${updateResult.message}`);
                    }
                } else {
                    results.skipped++;
                }
            } else {
                // Create new contact
                const createResult = await contactsService.create(contactData);
                if (createResult.success) {
                    results.created++;
                } else {
                    results.failed++;
                    results.errors.push(`Row ${rowNum}: ${createResult.message}`);
                }
            }
        } catch (error) {
            results.failed++;
            results.errors.push(`Row ${rowNum}: ${error.message}`);
        }
    }

    return results;
};

/**
 * Helper: Validate URL
 */
const isValidUrl = (string) => {
    try {
        new URL(string);
        return true;
    } catch (_) {
        return false;
    }
};

/**
 * Helper: Validate email
 */
const isValidEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
};

/**
 * Validate leads CSV data
 */
export const validateLeadsCSV = (data) => {
    const errors = [];
    const requiredFields = ['account_name', 'first_name', 'last_name'];

    if (!Array.isArray(data) || data.length === 0) {
        return { valid: false, errors: ['No data to import'] };
    }

    // Check headers
    const headers = Object.keys(data[0]);
    const missingFields = requiredFields.filter(field => !headers.includes(field));

    if (missingFields.length > 0) {
        errors.push(`Missing required columns: ${missingFields.join(', ')}`);
    }

    // Validate each row
    data.forEach((row, index) => {
        const rowNum = index + 2;

        if (!row.account_name || row.account_name.trim() === '') {
            errors.push(`Row ${rowNum}: Account name is required`);
        }
        if (!row.first_name || row.first_name.trim() === '') {
            errors.push(`Row ${rowNum}: First name is required`);
        }
        if (!row.last_name || row.last_name.trim() === '') {
            errors.push(`Row ${rowNum}: Last name is required`);
        }

        // Validate URLs if provided
        if (row.website && row.website !== '' && !isValidUrl(row.website)) {
            errors.push(`Row ${rowNum}: Invalid website URL`);
        }
        if (row.linkedin_url && row.linkedin_url !== '' && !isValidUrl(row.linkedin_url)) {
            errors.push(`Row ${rowNum}: Invalid LinkedIn URL`);
        }
        // Validate email
        if (row.email && row.email !== '' && !isValidEmail(row.email)) {
            errors.push(`Row ${rowNum}: Invalid email format`);
        }
    });

    return {
        valid: errors.length === 0,
        errors
    };
};

/**
 * Import leads (Account -> Contact -> Opportunity)
 */
export const importLeads = async (data) => {
    const results = {
        total: data.length,
        created: 0, // Opps created
        updated: 0, // Not really used for leads opps, maybe for accounts/contacts in future
        skipped: 0,
        failed: 0,
        errors: []
    };

    // Pre-fetch all accounts/contacts to minimize DB calls? 
    // For now, let's do it per row to be safe and simple, optimization can come later if needed.
    // Or at least fetch accounts to reduce lookup.

    for (let i = 0; i < data.length; i++) {
        const row = data[i];
        const rowNum = i + 2;

        try {
            // 1. Handle Account
            let accountId = null;
            const accountName = row.account_name?.trim();
            if (!accountName) throw new Error('Account name missing');

            // Search for existing account
            // We use the searchAccounts which is fuzzy, but here we probably want exact match or stick with search
            // Let's use searchAccounts with limit 1
            const accSearch = await accountService.searchAccounts(accountName, { limit: 1 });
            let existingAccount = null;
            if (accSearch.success && accSearch.data.length > 0) {
                // Check for exact case-insensitive match to be sure
                existingAccount = accSearch.data.find(a => a.name.toLowerCase() === accountName.toLowerCase());
            }

            if (existingAccount) {
                accountId = existingAccount.id;
            } else {
                // Create Account
                const newAccountData = {
                    name: accountName,
                    industry: row.industry?.trim() || null,
                    city: row.city?.trim() || null,
                    country: row.country?.trim() || null,
                    website: row.website?.trim() || null,
                    linkedin_url: row.linkedin_url?.trim() || null,
                    tax_id: row.tax_id?.trim() || null,
                    notes: row.notes?.trim() || null
                };
                const accCreate = await accountService.createAccount(newAccountData);
                if (!accCreate.success) throw new Error(`Failed to create account: ${accCreate.message}`);
                accountId = accCreate.data.id;
            }

            // 2. Handle Contact
            let contactId = null;
            const email = row.email?.trim();
            const firstName = row.first_name?.trim();
            const lastName = row.last_name?.trim();

            if (email) {
                // Check by email
                // Ideally we should have a getByEmail method, but getAll logic is...
                // contactsService.getAll does not filter by email directly unless we pass filter?
                // contactsService.getAll accepts filters.
                const contactSearch = await contactsService.getAll('*', { email: email });
                if (contactSearch.success && contactSearch.data.length > 0) {
                    contactId = contactSearch.data[0].id; // Use first match
                }
            }

            if (!contactId) {
                // Create Contact
                const newContactData = {
                    account_id: accountId,
                    first_name: firstName,
                    last_name: lastName,
                    email: email || null,
                    phone: row.phone?.trim() || null,
                    title: row.title?.trim() || null,
                    is_private: row.is_private?.toLowerCase() === 'true'
                };
                const contactCreate = await contactsService.create(newContactData);
                if (!contactCreate.success) throw new Error(`Failed to create contact: ${contactCreate.message}`);
                contactId = contactCreate.data.id;
            }

            // 3. Create Opportunity
            const oppName = `${accountName} - New Deal`;
            const oppData = {
                name: oppName,
                account_id: accountId,
                primary_contact_id: contactId,
                stage: 'Lead', // Default stage
                amount: row.amount ? parseFloat(row.amount) : null,
                close_date: parseDate(row.close_date) || null,
                description: 'Imported from CSV'
            };

            const oppCreate = await opportunitiesService.createOpportunity(oppData);
            if (oppCreate.success) {
                results.created++;
            } else {
                throw new Error(`Failed to create opportunity: ${oppCreate.message}`);
            }

        } catch (error) {
            results.failed++;
            results.errors.push(`Row ${rowNum}: ${error.message}`);
        }
    }

    return results;
};

export const csvImportService = {
    parseCSV,
    validateAccountsCSV,
    validateContactsCSV,
    validateLeadsCSV,
    detectAccountDuplicates,
    detectContactDuplicates,
    importAccounts,
    importContacts,
    importLeads
};
